export enum APP_FEATURES {
  PRIVATE_INBOX = "privateInbox",
  TRACK_VERSION_EDITOR = "trackVersionEditor",
  TYPEFORM_CONFIG_UI = "typeformConfigUI",
  TOGGLE_AUDIO_QUALITY_DIALOG = "toggleAudioQualityDialog",
}

export const AppFeaturesStatus: Record<APP_FEATURES, boolean> = {
  [APP_FEATURES.PRIVATE_INBOX]: false,
  [APP_FEATURES.TRACK_VERSION_EDITOR]: true,
  [APP_FEATURES.TYPEFORM_CONFIG_UI]: true,
  [APP_FEATURES.TOGGLE_AUDIO_QUALITY_DIALOG]: false,
};

/* SPACE MEMBERSHIP MIGRATION
---------------------------------- */
/**
 * The control for the space-membership migration is one string, stored on
 * `config-v1/spaceMembership`. The server reads it with
 * `loadMembershipFlags()` (`core/db/migrationFlags.ts`), and
 * `getMembershipClientFlags` mirrors it to the browser as a single boolean.
 *
 *   "legacy" - today's behaviour. The server neither writes nor reads
 *              member documents.
 *   "both"   - the server writes member documents alongside the legacy
 *              fields. Every read still answers from the legacy fields.
 *   "new"    - the server writes member documents, and they are the source
 *              of truth for reads and for `memberCount`.
 *
 * `new` does not mean "new only". The server writes the legacy fields in
 * all three modes, and no mode stops those writes. Here is why. The rules
 * half of this migration is an additive union: the rules grant access when
 * the legacy arrays grant it or when the member document grants it. Rules
 * cannot read this config document, and no step of the migration deletes
 * legacy data, so the union stays active. If the legacy writes stop, the
 * legacy reads do not stop. A revocation clears the member document but
 * leaves the frozen `readableByV2` in place, and the union still grants
 * access. Revocation then silently fails across the whole platform. Do not
 * add a fourth mode for that; it needs a rules change first, with its own
 * rollback plan.
 *
 * There is deliberately no per-space, per-user or percentage dimension. To
 * try a mode on a small surface first, use the dev project; see
 * `runbooks/membership-mode.md`.
 *
 * Security gates live on a different document (`config-v1/securityGates`,
 * `core/db/securityGates.ts`). That separation makes sure that a rollback
 * of the migration never rolls a security fix back with it; do not merge
 * the two documents.
 *
 * The browser bundle imports this module (it is in this package's
 * `exports` map, and 11 client files import it today). It must therefore
 * stay free of every Node built-in and of `./firebase`. The loader and the
 * merge live in `core/db/migrationFlags.ts` for that reason. The three
 * predicates below are pure string comparisons, so they can live here: one
 * definition, shared by both sides, with no second copy that can fall out
 * of sync.
 */
export type SpaceMembershipMode = "legacy" | "both" | "new";

/**
 * This function is an allowlist (a list of the accepted values). Never
 * write `mode !== "legacy"`.
 *
 * The client type this replaces was `Partial<Pick<...>>`, so an absent
 * boolean was falsy. An old bundle that received a new payload therefore
 * degraded to legacy on its own. A string does not degrade on its own.
 * Anything that is not exactly a recognised non-legacy value must read as
 * "legacy": an absent field, `null`, a typo, or a future fourth mode that
 * a newer tool wrote. This function is the only place that makes that
 * decision.
 */
export const asSpaceMembershipMode = (value: unknown): SpaceMembershipMode =>
  value === "both" || value === "new" ? value : "legacy";

/**
 * This function returns true when the server writes member documents: in
 * `both` and in `new`.
 */
export const writesMemberDocs = (mode: SpaceMembershipMode): boolean =>
  mode === "both" || mode === "new";

/**
 * This function returns true when member documents are the source of
 * truth for reads and for `memberCount`: in `new` only.
 *
 * A mode that reads member documents always also writes them, because
 * `new` is one of the write modes. That is the single most important
 * property of this type: it cannot express the state "read a model that
 * nothing writes". Under the flags this type replaced, that state was only
 * two independent rollout objects apart. That state locks every identity
 * out of every space. `loadEntryGateFacts` (`core/db/spaceMemberReads.ts`)
 * returns the member-document answer with no fallback for empty documents.
 * The shadow compare (the job that compares the new answers against the
 * legacy ones) reports the denial and does not correct it.
 */
export const readsMemberDocs = (mode: SpaceMembershipMode): boolean =>
  mode === "new";

/**
 * The migration flag document, stored at `config-v1/spaceMembership`.
 *
 * The document can be absent or only partly filled in. The loader merges
 * every field onto `DEFAULT_MEMBERSHIP_FLAGS` with a type check for each
 * field, so an absent document, an empty document, a partial document and
 * a wrong-typed field all fall back to legacy behaviour. This mirrors
 * `AdsConfig`/`DEFAULT_ADS_CONFIG` and `loadAdsConfig`
 * (`functions/handlers/ads/getAds.ts`), the only other runtime-config
 * pattern in the repo.
 */
export type MembershipFlags = {
  /**
   * The migration control. Any value that is not `"both"` or `"new"` reads
   * as `"legacy"`.
   */
  spaceMembership: SpaceMembershipMode;
  /**
   * Force the new read model off while the web bundle renders inside the
   * mobile app's WebView.
   *
   * Keep this field. Without it, a web-side mode flip also changes
   * behaviour inside the web surfaces that the Flutter app renders, and no
   * other switch can stop that. Mobile's own switch
   * (`members_v2_kill_switch_mobile`, in Firebase Remote Config) cannot
   * reach the web bundle, and Intercom cohorts have no platform dimension.
   * It is a boolean rather than a list of flag names because no per-flag
   * names are left to turn off.
   */
  disableInMobileWebView: boolean;
  /**
   * When true, `updateSpace` refuses to create a new `rolesV2,share_*`
   * key; share keys must then come from the server (`mintSpaceShareKey`).
   * A client can still modify and delete a key that already exists,
   * because mobile depends on those two operations.
   *
   * This flag is deliberately not part of the mode. Its precondition is
   * "the old web bundle is gone from users' caches", which is a fact about
   * deploy age and has nothing to do with which membership model the
   * server reads. If it folds into `new`, share-link creation fails
   * outright for every user on a cached build, and its rollback then
   * couples to the read cutover (the switch to member-document reads). It
   * had no kill switch before the many old flags collapsed into this
   * document. Because the field is separate, `legacy` stays identical to
   * the previous default for a project that has it on. It is not on
   * `securityGates` either: it is not a security fix, and a rollback of
   * the gates must not turn it off.
   */
  serverMintedShareKeysOnly: boolean;
  /**
   * The second of two guards on every migrate script that can write; the
   * `--apply` flag is the first. Both must be on before the script writes.
   */
  backfillWritesEnabled: boolean;
  /**
   * The guard on `9-purgeMemberDocs.js`, the emergency rollback script. It
   * is a separate field from `backfillWritesEnabled` on purpose (see
   * `migrate/spaceMembership/guards.js`): the emergency stop that halts
   * the backfill must not also block the emergency undo.
   */
  purgeEnabled: boolean;
};

export const DEFAULT_MEMBERSHIP_FLAGS: MembershipFlags = {
  spaceMembership: "legacy",
  disableInMobileWebView: false,
  serverMintedShareKeysOnly: false,
  backfillWritesEnabled: false,
  purgeEnabled: false,
};

/**
 * The subset handed to a browser, on the `getUserPublic` payload as
 * `publicUser.membershipFlags`.
 *
 * `membersV2Enabled` is a boolean, not the mode string. Every client consumer
 * tests one fact, whether it reads member documents, and `legacy` and
 * `both` look identical to all of them. The string therefore carries a
 * distinction that the client cannot act on but can mis-handle; a client
 * that cannot represent `"both"` cannot mis-handle it. The boolean is also
 * strictly less information: no space id, no identity key, no percentage,
 * no list.
 *
 * The producer type (this one) is exhaustive, so the server cannot forget
 * a field. The consumer type on the client is permissive, so neither side
 * crashes on a mismatch. Both properties still matter with two fields. Do
 * not unify the two types, even now that the short list makes that look
 * easy.
 */
export type MembershipClientFlags = {
  membersV2Enabled: boolean;
  disableInMobileWebView: boolean;
};

/** The flag document lives under `COLLECTION_ID.CONFIG` with this id. */
export const MEMBERSHIP_FLAGS_DOC_ID = "spaceMembership";
